import { formatDate, formatDateWithWeekday, formatExtractedDateRange, formatNumeroProcesso, toTitleCaseClasse } from '../../lib/formatters';
import { formatPartesResumo, type ParteResumivel } from '../../lib/partes/resumo';
import { providenciaDoPrazo } from '../../lib/prazos/presumido';
import { rotuloDoVencimento } from '../../lib/prazos/vencimento';
import type { PrazoCalculo } from '../../api/client';

// Lookup de labels por tipo de comunicação
const tipoLabels: Record<string, string> = {
  INTIMACAO: 'Intimação',
  CITACAO: 'Citação',
  NOTIFICACAO: 'Notificação',
  LISTA_DISTRIBUICAO: 'Distribuição',
  OUTRO: 'Outro',
};

const tipoDataLabels: Record<string, string> = {
  audiencia: 'Audiência',
  sessao: 'Sessão de julgamento',
  prazo: 'Prazo',
  generico: 'Data',
};

interface ComunicacaoPrintData {
  tipo?: string | null;
  numero_processo: string;
  destinatarios_json?: ParteResumivel[] | null;
  assunto?: string | null;
  nome_classe?: string | null;
  nome_orgao?: string | null;
  sigla_tribunal?: string | null;
  data_disponibilizacao?: string | null;
  data_ciencia?: string | null;
  prazo_resposta?: string | null;
  /** A conta do sistema: o PDF diz "estimado" e "confira no tribunal" como a tela. */
  prazo_calculo?: PrazoCalculo | null;
  texto?: string | null;
  datas_extraidas?: Array<{
    tipo: string;
    data: string;
    hora: string | null;
    data_fim: string | null;
    hora_fim: string | null;
  }> | null;
  prazos_extraidos?: Array<{ dias: number; unidade: string; contexto: string; contexto_inicio: number; tamanho: number }> | null;
}

interface ComunicacaoPrintContentProps {
  comunicacao: ComunicacaoPrintData;
}

const styles = {
  container: {
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    maxWidth: '210mm',
    margin: '0 auto',
    padding: '20mm 15mm',
    color: '#1a1a1a',
    fontSize: '12pt',
    lineHeight: '1.6',
  } as React.CSSProperties,
  header: {
    borderBottom: '2px solid #333',
    paddingBottom: '12px',
    marginBottom: '20px',
  } as React.CSSProperties,
  title: {
    fontSize: '16pt',
    fontWeight: 700,
    margin: '0 0 4px 0',
  } as React.CSSProperties,
  processo: {
    fontSize: '11pt',
    color: '#555',
    margin: 0,
  } as React.CSSProperties,
  badge: {
    display: 'inline-block',
    padding: '2px 10px',
    border: '1px solid #888',
    borderRadius: '4px',
    fontSize: '9pt',
    marginRight: '6px',
    marginTop: '8px',
  } as React.CSSProperties,
  section: {
    marginBottom: '16px',
  } as React.CSSProperties,
  sectionTitle: {
    fontSize: '11pt',
    fontWeight: 700,
    marginBottom: '8px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    color: '#444',
  } as React.CSSProperties,
  metaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '4px 0',
    borderBottom: '1px solid #eee',
    fontSize: '11pt',
  } as React.CSSProperties,
  metaLabel: {
    color: '#666',
    fontWeight: 500,
  } as React.CSSProperties,
  metaValue: {
    textAlign: 'right' as const,
  } as React.CSSProperties,
  texto: {
    whiteSpace: 'pre-wrap' as const,
    textAlign: 'justify' as const,
    fontSize: '11pt',
    lineHeight: '1.8',
  } as React.CSSProperties,
  dateItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '4px 8px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    marginBottom: '4px',
    fontSize: '11pt',
  } as React.CSSProperties,
  footer: {
    marginTop: '30px',
    paddingTop: '10px',
    borderTop: '1px solid #ccc',
    fontSize: '8pt',
    color: '#999',
    textAlign: 'center' as const,
  } as React.CSSProperties,
};

export function ComunicacaoPrintContent({ comunicacao }: ComunicacaoPrintContentProps) {
  const datasRelevantes = comunicacao.datas_extraidas?.filter(
    d => d.tipo === 'audiencia' || d.tipo === 'sessao' || d.tipo === 'prazo',
  );

  return (
    <div style={styles.container}>
      {/* Cabeçalho */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          {toTitleCaseClasse(comunicacao.assunto || comunicacao.nome_classe || 'Comunicação')}
        </h1>
        <p style={styles.processo}>
          Processo: {formatNumeroProcesso(comunicacao.numero_processo)}
        </p>
        {formatPartesResumo(comunicacao.destinatarios_json) && (
          <p style={styles.processo}>
            Partes: {formatPartesResumo(comunicacao.destinatarios_json)}
          </p>
        )}
        <div>
          <span style={styles.badge}>
            {tipoLabels[comunicacao.tipo ?? ''] ?? comunicacao.tipo ?? 'Outro'}
          </span>
          {comunicacao.sigla_tribunal && (
            <span style={styles.badge}>{comunicacao.sigla_tribunal}</span>
          )}
        </div>
      </div>

      {/* Metadados */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>Informações</div>
        {comunicacao.nome_orgao && (
          <div style={styles.metaRow}>
            <span style={styles.metaLabel}>Órgão</span>
            <span style={styles.metaValue}>{comunicacao.nome_orgao}</span>
          </div>
        )}
        {comunicacao.data_disponibilizacao && (
          <div style={styles.metaRow}>
            <span style={styles.metaLabel}>Disponibilização</span>
            <span style={styles.metaValue}>
              {formatDateWithWeekday(comunicacao.data_disponibilizacao)}
            </span>
          </div>
        )}
        {comunicacao.prazo_resposta && (
          <div style={styles.metaRow}>
            <span style={styles.metaLabel}>Prazo</span>
            <span style={{ ...styles.metaValue, fontWeight: 700 }}>
              {comunicacao.prazo_calculo
                ? rotuloDoVencimento(comunicacao.prazo_calculo.vencimento, comunicacao.prazo_calculo.estimado).longo
                : formatDate(comunicacao.prazo_resposta)}
            </span>
          </div>
        )}
        {comunicacao.prazo_calculo && (
          <div style={styles.metaRow}>
            <span style={styles.metaLabel} />
            <span style={styles.metaValue}>
              {[
                comunicacao.prazo_calculo.origem === 'presumido'
                  ? `A intimação não diz o prazo: ${providenciaDoPrazo(comunicacao.prazo_calculo)}, prazo da lei${comunicacao.prazo_calculo.dispositivo ? ` (${comunicacao.prazo_calculo.dispositivo})` : ''}.`
                  : null,
                comunicacao.prazo_calculo.data_fixada
                  ? 'Data fixada na própria intimação — confira no tribunal.'
                  : 'Conta do sistema, a partir da disponibilização no DJEN — confira no tribunal.',
                comunicacao.prazo_calculo.estimado ? comunicacao.prazo_calculo.motivo_estimado : null,
                comunicacao.prazo_calculo.aviso_regime,
              ]
                .filter(Boolean)
                .join(' ')}
            </span>
          </div>
        )}
      </div>

      {/* Texto da comunicação */}
      {comunicacao.texto && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Texto da Comunicação</div>
          <div style={styles.texto}>{comunicacao.texto}</div>
        </div>
      )}

      {/* Datas extraídas */}
      {datasRelevantes && datasRelevantes.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Datas Identificadas</div>
          {datasRelevantes.map((d, i) => (
            <div key={i} style={styles.dateItem}>
              <span>{formatExtractedDateRange(d)}</span>
              <span>{tipoDataLabels[d.tipo] ?? 'Data'}</span>
            </div>
          ))}
        </div>
      )}

      {/* Prazos extraídos */}
      {comunicacao.prazos_extraidos && comunicacao.prazos_extraidos.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Prazos Mencionados</div>
          {comunicacao.prazos_extraidos.map((p, i) => (
            <div key={i} style={styles.dateItem}>
              <span style={{ fontWeight: 700 }}>
                {p.dias} {({ horas: 'horas', dias: 'dias', dias_uteis: 'dias', meses: 'meses', anos: 'anos' })[p.unidade] ?? 'dias'}
              </span>
              <span style={{ fontSize: '0.75rem', color: '#666' }}>
                {p.contexto.slice(0, 80)}...
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Rodapé */}
      <div style={styles.footer}>
        Exportado em {new Date().toLocaleDateString('pt-BR')} às{' '}
        {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
        {' — '}JurisApp
      </div>
    </div>
  );
}
